import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, cn, GroupedTable } from "@etudescarre/ui";
import {
  DotsSixVerticalIcon,
  InfoIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from "@phosphor-icons/react";
import type { ReactNode } from "react";
import {
  cellInputClass as inputClass,
  requiredCellProps,
  upperCaseInput,
} from "@/lib/utils/cell-input";
import {
  stickyHeadFirstRowClass,
  stickyHeadSecondRowClass,
  stickyRowHeaderContentClass,
  useStickyHeaderOffsets,
} from "@/lib/utils/sticky-table-header";

const groupHeadClass = cn(stickyHeadFirstRowClass, "h-10");
const headClass = cn(stickyHeadSecondRowClass, "h-10");

const PERFORMANCE_CASE_HELP =
  "La 1re lettre passe en majuscule. Un texte de plusieurs mots tapé tout en majuscules est remis en minuscules (« CHARPENTE BOIS » devient « Charpente bois »). Un mot seul en majuscules est gardé comme sigle (« CVC »). Dès que le texte contient une minuscule, il est gardé tel quel (« Plomberie EU/EV »).";

export type LotPerformance = {
  id: number;
  lotId: number;
  name: string;
  order: number;
  /** Number of interventions attached to this performance. */
  interventionCount: number;
};

export type Lot = {
  id: number;
  name: string;
  shortName: string | null;
  nLot: string | null;
  /** Performances already sorted by display order. */
  performances: LotPerformance[];
};

export type LotDraft = { nLot: string; name: string; shortName: string };
export type PerformanceDraft = { name: string };
export type NewLotRow = { tempId: string } & LotDraft;
export type NewPerformanceRow = { tempId: string } & PerformanceDraft;

export type YardLotsTableProps = {
  lots: Lot[];
  getLotDraft: (lot: Lot) => LotDraft;
  isLotDirty: (lot: Lot) => boolean;
  onLotFieldChange: (lot: Lot, patch: Partial<LotDraft>) => void;
  onDeleteLot: (lot: Lot) => void;
  getPerformanceDraft: (performance: LotPerformance) => PerformanceDraft;
  isPerformanceDirty: (performance: LotPerformance) => boolean;
  onPerformanceFieldChange: (
    performance: LotPerformance,
    patch: Partial<PerformanceDraft>,
  ) => void;
  onDeletePerformance: (performance: LotPerformance) => void;
  onReorderPerformances: (lotId: number, orderedIds: number[]) => void;
  newLots: NewLotRow[];
  onNewLotFieldChange: (tempId: string, patch: Partial<LotDraft>) => void;
  onRemoveNewLot: (tempId: string) => void;
  onAddLotRow: () => void;
  newPerformancesByLot: Map<number, NewPerformanceRow[]>;
  onNewPerformanceFieldChange: (
    lotId: number,
    tempId: string,
    patch: Partial<PerformanceDraft>,
  ) => void;
  onRemoveNewPerformance: (lotId: number, tempId: string) => void;
  onAddPerformanceRow: (lotId: number) => void;
};

function DragHandle(props: {
  attributes: React.HTMLAttributes<HTMLButtonElement>;
  listeners: Record<string, unknown> | undefined;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-label={props.label}
      className="cursor-grab touch-none text-zinc-400 hover:text-zinc-600 active:cursor-grabbing dark:hover:text-zinc-200"
      {...props.attributes}
      {...props.listeners}
    >
      <DotsSixVerticalIcon className="size-5" />
    </button>
  );
}

/**
 * A saved performance row. `leading` carries the lot cells (with rowSpan)
 * when this is the first row of the lot's block, per the wireframe where
 * the lot line is shared with its first prestation (same layout as the
 * screen 904 buildings/zones table).
 */
function SortablePerformanceRow({
  performance,
  draft,
  dirty,
  displayOrder,
  onFieldChange,
  onDelete,
  leading,
}: {
  performance: LotPerformance;
  draft: PerformanceDraft;
  dirty: boolean;
  displayOrder: number;
  onFieldChange: (patch: Partial<PerformanceDraft>) => void;
  onDelete: (performance: LotPerformance) => void;
  leading?: ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `performance-${performance.id}` });

  // The drag transform goes on the prestation cells, not the <tr>: the first
  // row also hosts the lot cells (rowSpan), which must stay put.
  const cellStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <GroupedTable.Row ref={setNodeRef}>
      {leading}
      <GroupedTable.Cell className="w-8" style={cellStyle}>
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          label={`Déplacer la prestation ${performance.name}`}
        />
      </GroupedTable.Cell>
      <GroupedTable.Cell style={cellStyle}>
        <input
          type="text"
          value={draft.name}
          title={draft.name}
          onChange={(e) => onFieldChange({ name: e.target.value })}
          className={inputClass(dirty)}
          {...requiredCellProps("Désignation", draft.name, dirty)}
        />
      </GroupedTable.Cell>
      <GroupedTable.Cell
        className="w-16 text-center text-zinc-500"
        style={cellStyle}
      >
        {displayOrder}
      </GroupedTable.Cell>
      <GroupedTable.Cell className="w-12" style={cellStyle}>
        <Button
          plain
          onClick={() => onDelete(performance)}
          aria-label="Supprimer la prestation"
        >
          <TrashIcon className="size-4" />
        </Button>
      </GroupedTable.Cell>
    </GroupedTable.Row>
  );
}

/** A not-yet-saved performance row (added via "+", pending "Enregistrer"). */
function NewPerformanceRowView({
  row,
  onFieldChange,
  onRemove,
  leading,
}: {
  row: NewPerformanceRow;
  onFieldChange: (patch: Partial<PerformanceDraft>) => void;
  onRemove: () => void;
  leading?: ReactNode;
}) {
  return (
    <GroupedTable.Row className="bg-amber-50/50 dark:bg-amber-900/10">
      {leading}
      <GroupedTable.Cell className="w-8" />
      <GroupedTable.Cell>
        <input
          type="text"
          value={row.name}
          onChange={(e) => onFieldChange({ name: e.target.value })}
          className={inputClass(true)}
          {...requiredCellProps("Désignation", row.name, true)}
        />
      </GroupedTable.Cell>
      <GroupedTable.Cell className="w-16 text-center text-zinc-400">
        —
      </GroupedTable.Cell>
      <GroupedTable.Cell className="w-12">
        <Button
          plain
          onClick={onRemove}
          aria-label="Annuler l'ajout de la prestation"
        >
          <XIcon className="size-4" />
        </Button>
      </GroupedTable.Cell>
    </GroupedTable.Row>
  );
}

/**
 * One lot block: a <tbody> whose lot cells span all the prestation rows
 * (wireframe layout), followed by an "add prestation" row. Prestations are
 * reorderable by drag-and-drop within the lot.
 */
function LotBody({
  lot,
  draft,
  dirty,
  onFieldChange,
  onDeleteLot,
  getPerformanceDraft,
  isPerformanceDirty,
  onPerformanceFieldChange,
  onDeletePerformance,
  onReorderPerformances,
  newPerformances,
  onNewPerformanceFieldChange,
  onRemoveNewPerformance,
  onAddPerformanceRow,
}: {
  lot: Lot;
  draft: LotDraft;
  dirty: boolean;
  onFieldChange: (patch: Partial<LotDraft>) => void;
  onDeleteLot: (lot: Lot) => void;
  getPerformanceDraft: (performance: LotPerformance) => PerformanceDraft;
  isPerformanceDirty: (performance: LotPerformance) => boolean;
  onPerformanceFieldChange: (
    performance: LotPerformance,
    patch: Partial<PerformanceDraft>,
  ) => void;
  onDeletePerformance: (performance: LotPerformance) => void;
  onReorderPerformances: (lotId: number, orderedIds: number[]) => void;
  newPerformances: NewPerformanceRow[];
  onNewPerformanceFieldChange: (
    tempId: string,
    patch: Partial<PerformanceDraft>,
  ) => void;
  onRemoveNewPerformance: (tempId: string) => void;
  onAddPerformanceRow: () => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = lot.performances.map((p) => p.id);
    const oldIndex = ids.indexOf(
      Number(String(active.id).replace("performance-", "")),
    );
    const newIndex = ids.indexOf(
      Number(String(over.id).replace("performance-", "")),
    );
    if (oldIndex === -1 || newIndex === -1) return;
    onReorderPerformances(lot.id, arrayMove(ids, oldIndex, newIndex));
  };

  const performanceRowCount = lot.performances.length + newPerformances.length;
  // Lot cells span every prestation row plus the trailing "add prestation"
  // row (or the single "Aucune prestation" row when the lot is empty).
  const totalRows = Math.max(1, performanceRowCount) + 1;

  const lotCells = (
    <>
      <GroupedTable.Cell rowSpan={totalRows} className="w-20 align-top">
        <div className={stickyRowHeaderContentClass}>
          <input
            type="text"
            value={draft.nLot}
            onChange={(e) => onFieldChange({ nLot: e.target.value })}
            className={cn(inputClass(dirty), "text-center")}
            {...requiredCellProps("N°", draft.nLot, dirty)}
          />
        </div>
      </GroupedTable.Cell>
      <GroupedTable.Cell rowSpan={totalRows} className="align-top">
        <div className={stickyRowHeaderContentClass}>
          <input
            type="text"
            value={draft.name}
            title={draft.name}
            onChange={(e) => onFieldChange({ name: upperCaseInput(e.target) })}
            className={inputClass(dirty)}
            {...requiredCellProps("Désignation", draft.name, dirty)}
          />
        </div>
      </GroupedTable.Cell>
      <GroupedTable.Cell rowSpan={totalRows} className="w-32 align-top">
        <div className={stickyRowHeaderContentClass}>
          <input
            type="text"
            value={draft.shortName}
            onChange={(e) =>
              onFieldChange({ shortName: upperCaseInput(e.target) })
            }
            className={inputClass(dirty)}
            {...requiredCellProps("Abréviation", draft.shortName, dirty)}
          />
        </div>
      </GroupedTable.Cell>
      <GroupedTable.Cell rowSpan={totalRows} className="w-12 align-top">
        <div className={stickyRowHeaderContentClass}>
          <Button
            plain
            onClick={() => onDeleteLot(lot)}
            aria-label="Supprimer le lot"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      </GroupedTable.Cell>
    </>
  );

  return (
    <tbody className="border-b border-gray-200 last:border-0">
      {performanceRowCount === 0 && (
        <GroupedTable.Row>
          {lotCells}
          <GroupedTable.Cell colSpan={4} className="text-zinc-400">
            Aucune prestation
          </GroupedTable.Cell>
        </GroupedTable.Row>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        // Portal dnd-kit's hidden accessibility nodes out of the <tbody>,
        // where raw <div>s are invalid HTML.
        accessibility={{ container: document.body }}
      >
        <SortableContext
          items={lot.performances.map((p) => `performance-${p.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {lot.performances.map((performance, index) => (
            <SortablePerformanceRow
              key={performance.id}
              performance={performance}
              draft={getPerformanceDraft(performance)}
              dirty={isPerformanceDirty(performance)}
              displayOrder={index + 1}
              onFieldChange={(patch) =>
                onPerformanceFieldChange(performance, patch)
              }
              onDelete={onDeletePerformance}
              leading={index === 0 ? lotCells : undefined}
            />
          ))}
        </SortableContext>
      </DndContext>

      {newPerformances.map((row, index) => (
        <NewPerformanceRowView
          key={row.tempId}
          row={row}
          onFieldChange={(patch) =>
            onNewPerformanceFieldChange(row.tempId, patch)
          }
          onRemove={() => onRemoveNewPerformance(row.tempId)}
          leading={
            lot.performances.length === 0 && index === 0 ? lotCells : undefined
          }
        />
      ))}

      <GroupedTable.Row>
        <GroupedTable.Cell colSpan={4}>
          <button
            type="button"
            onClick={onAddPerformanceRow}
            className="flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
          >
            <PlusIcon className="size-3.5" />
            Ajouter une prestation
          </button>
        </GroupedTable.Cell>
      </GroupedTable.Row>
    </tbody>
  );
}

/** A not-yet-saved lot (added via "+", pending "Enregistrer"). */
function NewLotBody({
  row,
  onFieldChange,
  onRemove,
}: {
  row: NewLotRow;
  onFieldChange: (patch: Partial<LotDraft>) => void;
  onRemove: () => void;
}) {
  return (
    <tbody className="border-b border-gray-200 last:border-0">
      <GroupedTable.Row className="bg-amber-50/50 dark:bg-amber-900/10">
        <GroupedTable.Cell className="w-20">
          <input
            type="text"
            value={row.nLot}
            onChange={(e) => onFieldChange({ nLot: e.target.value })}
            className={cn(inputClass(true), "text-center")}
            {...requiredCellProps("N°", row.nLot, true)}
          />
        </GroupedTable.Cell>
        <GroupedTable.Cell>
          <input
            type="text"
            value={row.name}
            onChange={(e) => onFieldChange({ name: upperCaseInput(e.target) })}
            className={inputClass(true)}
            {...requiredCellProps("Désignation", row.name, true)}
          />
        </GroupedTable.Cell>
        <GroupedTable.Cell className="w-32">
          <input
            type="text"
            value={row.shortName}
            onChange={(e) =>
              onFieldChange({ shortName: upperCaseInput(e.target) })
            }
            className={inputClass(true)}
            {...requiredCellProps("Abréviation", row.shortName, true)}
          />
        </GroupedTable.Cell>
        <GroupedTable.Cell className="w-12">
          <Button plain onClick={onRemove} aria-label="Annuler l'ajout du lot">
            <XIcon className="size-4" />
          </Button>
        </GroupedTable.Cell>
        <GroupedTable.Cell colSpan={4} className="text-xs text-zinc-400">
          Enregistrez ce lot pour pouvoir y ajouter des prestations.
        </GroupedTable.Cell>
      </GroupedTable.Row>
    </tbody>
  );
}

/**
 * Screen 905 — lots & prestations as a single grouped editable table (two
 * column groups, the lot line shared with its first prestation), same
 * layout as the screen 904 buildings/zones table, per the wireframe.
 */
export function YardLotsTable({
  lots,
  getLotDraft,
  isLotDirty,
  onLotFieldChange,
  onDeleteLot,
  getPerformanceDraft,
  isPerformanceDirty,
  onPerformanceFieldChange,
  onDeletePerformance,
  onReorderPerformances,
  newLots,
  onNewLotFieldChange,
  onRemoveNewLot,
  onAddLotRow,
  newPerformancesByLot,
  onNewPerformanceFieldChange,
  onRemoveNewPerformance,
  onAddPerformanceRow,
}: YardLotsTableProps) {
  const isEmpty = lots.length === 0 && newLots.length === 0;
  const { headerRef, style: stickyOffsets } = useStickyHeaderOffsets();

  return (
    // Only the table body scrolls: the grid fills the height the section
    // hands down and its header stays pinned (screen-design rules).
    // Compact cell padding keeps more rows on screen at 1280×800.
    <GroupedTable
      grid
      className="min-h-0 [&_td]:px-1 [&_td]:py-0.5 [&_th]:px-2 [&_th]:py-1.5"
      style={stickyOffsets}
    >
      <GroupedTable.Header ref={headerRef}>
        <GroupedTable.HeaderRow>
          <GroupedTable.HeaderGroup colSpan={4} className={groupHeadClass}>
            <div className="flex items-center justify-center gap-2">
              Lots
              <button
                type="button"
                onClick={onAddLotRow}
                aria-label="Ajouter un lot"
                title="Ajouter un lot"
                className="flex size-6 cursor-pointer items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900"
              >
                <PlusIcon className="size-4" />
              </button>
            </div>
          </GroupedTable.HeaderGroup>
          <GroupedTable.HeaderGroup colSpan={4} className={groupHeadClass}>
            Prestations associées
          </GroupedTable.HeaderGroup>
        </GroupedTable.HeaderRow>
        <GroupedTable.HeaderRow>
          {/* Lots */}
          <GroupedTable.Head className={cn(headClass, "w-20 text-center")}>
            N°
          </GroupedTable.Head>
          <GroupedTable.Head className={headClass}>
            Désignation
          </GroupedTable.Head>
          <GroupedTable.Head className={cn(headClass, "w-32")}>
            Abréviation
          </GroupedTable.Head>
          <GroupedTable.Head className={cn(headClass, "w-12")} />
          {/* Prestations */}
          <GroupedTable.Head className={cn(headClass, "w-8")} />
          <GroupedTable.Head className={headClass}>
            <span
              className="inline-flex cursor-help items-center gap-1"
              title={PERFORMANCE_CASE_HELP}
            >
              Désignation
              <InfoIcon aria-hidden className="size-4 text-zinc-500" />
            </span>
          </GroupedTable.Head>
          <GroupedTable.Head className={cn(headClass, "w-16 text-center")}>
            Ordre
          </GroupedTable.Head>
          <GroupedTable.Head className={cn(headClass, "w-12")} />
        </GroupedTable.HeaderRow>
      </GroupedTable.Header>

      {isEmpty && (
        <tbody>
          <GroupedTable.Row>
            <GroupedTable.Cell
              colSpan={8}
              className="py-8 text-center text-zinc-500"
            >
              Aucun lot. Commencez par en ajouter un avec le bouton + à côté de
              « Lots ».
            </GroupedTable.Cell>
          </GroupedTable.Row>
        </tbody>
      )}

      {lots.map((lot) => (
        <LotBody
          key={lot.id}
          lot={lot}
          draft={getLotDraft(lot)}
          dirty={isLotDirty(lot)}
          onFieldChange={(patch) => onLotFieldChange(lot, patch)}
          onDeleteLot={onDeleteLot}
          getPerformanceDraft={getPerformanceDraft}
          isPerformanceDirty={isPerformanceDirty}
          onPerformanceFieldChange={onPerformanceFieldChange}
          onDeletePerformance={onDeletePerformance}
          onReorderPerformances={onReorderPerformances}
          newPerformances={newPerformancesByLot.get(lot.id) ?? []}
          onNewPerformanceFieldChange={(tempId, patch) =>
            onNewPerformanceFieldChange(lot.id, tempId, patch)
          }
          onRemoveNewPerformance={(tempId) =>
            onRemoveNewPerformance(lot.id, tempId)
          }
          onAddPerformanceRow={() => onAddPerformanceRow(lot.id)}
        />
      ))}

      {newLots.map((row) => (
        <NewLotBody
          key={row.tempId}
          row={row}
          onFieldChange={(patch) => onNewLotFieldChange(row.tempId, patch)}
          onRemove={() => onRemoveNewLot(row.tempId)}
        />
      ))}
    </GroupedTable>
  );
}
